import React from 'react';
import { motion } from 'motion/react';
import { useTilt } from '../../hooks/useTilt';
import type { BentoItem } from '../../types';

interface BentoCardProps extends BentoItem {
  index: number;
}

export default function BentoCard({
  title,
  description,
  image,
  overlayImage,
  badge,
  colSpan,
  index,
}: BentoCardProps) {
  const isWide = colSpan === 2;
  const { rotateX, rotateY, handleMouseMove, handleMouseLeave } = useTilt({ strength: 6 });

  return (
    <motion.div
      className={`relative group rounded-2xl overflow-hidden cursor-pointer card-shimmer card-glow
        bg-dark-card border border-white/[0.06]
      `}
      variants={{
        hidden: { opacity: 0, y: 40 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.5, delay: index * 0.08 },
        },
      }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY, transformPerspective: 1000 }}
    >
      {/* Image area */}
      <div
        className={`relative overflow-hidden ${
          isWide ? 'h-52 sm:h-60 lg:h-72' : 'h-44 sm:h-52 lg:h-60'
        }`}
      >
        <img
          src={image}
          alt={title}
          width={800}
          height={600}
          className="w-full h-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-110"
          loading="lazy"
        />
        {/* Overlay image (e.g., alert stock) */}
        {overlayImage && (
          <div className="absolute bottom-4 right-4 w-28 lg:w-36 rounded-lg overflow-hidden shadow-2xl ring-1 ring-white/10 animate-float">
            <img src={overlayImage} alt="Overlay" width={144} height={100} className="w-full h-auto" loading="lazy" />
          </div>
        )}
        {/* Multi-layer gradient for depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-dark-card via-dark-card/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-card/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Badge top-left */}
        {badge && (
          <div className="absolute top-3 left-3 z-10">
            <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-white/10 backdrop-blur-md text-white/80 text-[11px] font-medium tracking-wide border border-white/[0.08]">
              {badge}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-6">
        <h3 className="text-white font-bold text-base lg:text-lg leading-snug">{title}</h3>
        <p className={`text-gray-400 text-sm mt-1.5 leading-relaxed ${
          isWide ? 'max-w-lg' : ''
        }`}>
          {description}
        </p>
      </div>

      {/* Hover glow border */}
      <div className="absolute inset-0 rounded-2xl border border-transparent group-hover:border-primary/20 transition-all duration-500 pointer-events-none" />
      {/* Subtle corner glow on hover */}
      <div className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at top left, rgba(249,115,22,0.08), transparent 50%)',
        }}
      />
    </motion.div>
  );
}
